import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Collapse,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HistoryIcon from "@mui/icons-material/History";
import AssignmentIcon from "@mui/icons-material/Assignment";
import MedicationIcon from "@mui/icons-material/Medication";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import EventNoteIcon from "@mui/icons-material/EventNote";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import {
  patientDetailsApi,
  practiceFusionApi,
  clinicalApi,
  PatientEncounter,
} from "../../services/api";
import PatientDiagnosisManager from "./PatientDiagnosisManager";
import PatientDetailListManager from "./PatientDetailListManager";
import PatientEncountersPanel from "./PatientEncountersPanel";

// Notify other parts of the patient view (e.g. the history timeline in
// PatientDetailsView) that the chart was edited so they can refetch
// without requiring a page refresh. Mirrors the existing
// `vitalsUnitSystemChange` window-event pattern.
const notifyChartChange = (spruceEntityId?: string) => {
  try {
    window.dispatchEvent(
      new CustomEvent("patientChartChange", { detail: { spruceEntityId } }),
    );
  } catch {
    /* noop */
  }
};

type Detail = {
  id?: string;
  category?: string;
  content?: string;
  createdAt?: string;
  created_at?: string;
  createdBy?: string;
  created_by?: string;
  created_by_user_id?: number | null;
};
type VitalField =
  | "height"
  | "weight"
  | "bp"
  | "smoking"
  | "pulse"
  | "temp"
  | "resp"
  | "spo2"
  | "pain";
type TextSection = "primary_dx" | "current_meds" | "family_history";
type ExpandedKey = "allergies" | "vitals" | "encounters" | TextSection;

const TEXT_SECTION_CONFIG: Record<
  TextSection,
  { label: string; category: string; placeholder: string }
> = {
  primary_dx: {
    label: "Primary DX",
    category: "primary_diagnosis",
    placeholder: "e.g. HTN, T2DM, hyperlipidemia",
  },
  current_meds: {
    label: "Current Meds",
    category: "current_medications",
    placeholder: "One medication per line, or comma/semicolon separated",
  },
  family_history: {
    label: "Family History",
    category: "family_history",
    placeholder: "e.g. Father – CAD, Mother – T2DM",
  },
};

type Props = {
  spruceEntityId?: string;
  resetSignal?: string | number;
  patientName?: string;
  patientEmail?: string;
  patientPhone?: string;
  /** Mobile: skip the always-visible "Chart · n/6 filled" trigger button —
   *  the parent owns the entry point (e.g. the chart icon in the unified
   *  sticky bar) via the openChart() ref handle. The bottom sheet and the
   *  inline expanded panels still render here. */
  hideMobileTrigger?: boolean;
  /** Desktop chart surface: render NO pills and NO trigger — this instance is
   *  a hidden host for the SAME chart sheet the mobile hero uses, opened via
   *  openChart() from the desktop header. The sheet anchors to the right and
   *  section editors open as centered modals. Unifies desktop with mobile. */
  chartSurface?: boolean;
  /** Raise the chart sheet above a parent Dialog (MUI modal z-index 1300) —
   *  e.g. when opened over the triage task dock. Section editors render at
   *  drawerZIndex + 1 so they stay above the sheet. Unset = MUI defaults. */
  drawerZIndex?: number;
};

// Imperative handle so a parent (e.g. the collapsing patient hero in
// Contacts.tsx) can open the mobile Chart bottom sheet from its own compact
// sticky bar without lifting `chartSheetOpen` state out of this component.
export type PatientClinicalStripHandle = {
  openChart: () => void;
  closeChart: () => void;
};

const SEVERE_RX = /\b(anaphyl|severe|epipen|life[- ]threatening)\b/i;

const PatientClinicalStrip = forwardRef<PatientClinicalStripHandle, Props>(
  (
    { spruceEntityId, resetSignal, patientName, patientEmail, patientPhone, hideMobileTrigger = false, chartSurface = false, drawerZIndex },
    ref,
  ) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [details, setDetails] = useState<Detail[]>([]);
    const [expanded, setExpanded] = useState<ExpandedKey | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [encounters, setEncounters] = useState<PatientEncounter[]>([]);
    const [encountersLoading, setEncountersLoading] = useState(false);
    // Mobile-only "Chart" bottom sheet. We keep this independent of `expanded`
    // because tapping a row inside the sheet should both close the sheet AND
    // open the in-strip Collapse panel for that section.
    const [chartSheetOpen, setChartSheetOpen] = useState(false);

    useImperativeHandle(
      ref,
      () => ({
        openChart: () => setChartSheetOpen(true),
        closeChart: () => setChartSheetOpen(false),
      }),
      [],
    );

    // Collapse on patient change and on parent-driven reset (tab change, etc.)
    useEffect(() => {
      setExpanded(null);
      setChartSheetOpen(false);
    }, [spruceEntityId, resetSignal]);

    // Auto-collapse on scroll-down to give clinicians more vertical real
    // estate while they read the timeline / messages below. Works for both
    // the window and the nearest scrollable ancestor (which is how the
    // patient details panel scrolls on mobile and inside dialogs).
    //  - Only fires while a section is currently expanded (no work otherwise).
    //  - Disabled on mobile: the mobile layout already collapses the hero
    //    and pins a sticky tab strip, so the chart pills aren't permanently
    //    on screen anyway. With auto-collapse on, opening Edit + iOS
    //    keyboard pushing the outer scroll registered as a "scroll down"
    //    and shut the panel the doctor just opened.
    //  - Disabled whenever any text section is being edited (gate lives
    //    further down via `anyTextEditing`, after `textEditing` is
    //    declared), so a long typed entry that grows the editor and
    //    shifts surrounding layout doesn't trigger a self-close
    //    mid-keystroke.
    //  - Uses a generous threshold so finger tremor / momentum bounce /
    //    tiny layout shifts don't dismiss the panel.
    //  - Ignores upward scrolls — the user explicitly opened the panel,
    //    so we don't re-open it automatically; they can tap to reopen.
    // Forward-ref to the editing-state derived value below — keeps the
    // hook order intact while letting the effect read the current value
    // every render via .current without triggering a TDZ error.
    const anyTextEditingRef = useRef(false);
    useEffect(() => {
      if (!expanded) return;
      if (isMobile) return;
      const SCROLL_THRESHOLD = 80;
      // In this app the scroll container is usually NOT an ancestor of the
      // strip — it's a sibling (e.g. the tab body in the contacts panel).
      // Scroll events don't bubble, so we listen in the *capture* phase on
      // the document, which sees scroll from any element. We then track the
      // last scrollTop per target so each scroller is judged on its own.
      const lastByTarget = new WeakMap<EventTarget, number>();
      // Window scroll has no element target, so track it separately.
      let lastWindowY = window.scrollY || window.pageYOffset;
      const collapseIfDownward = (
        currentY: number,
        prevY: number | undefined,
      ) => {
        if (prevY !== undefined && currentY - prevY > SCROLL_THRESHOLD) {
          setExpanded(null);
        }
      };
      const onScroll = (e: Event) => {
        // Re-check every event so entering edit mode after the listener
        // is attached immediately disarms auto-collapse (the effect
        // doesn't re-run on textEditing changes by design).
        if (anyTextEditingRef.current) return;
        const t = e.target;
        // Document/window scroll arrives with target === document
        if (!t || t === document) {
          const y = window.scrollY || window.pageYOffset;
          collapseIfDownward(y, lastWindowY);
          lastWindowY = y;
          return;
        }
        const el = t as HTMLElement;
        // Ignore scroll events from inside the strip itself (e.g. the user
        // scrolling within an open vitals panel) — only collapse when the
        // user is scrolling the surrounding page/tab body.
        if (stripRef.current && stripRef.current.contains(el)) return;
        const y = el.scrollTop;
        collapseIfDownward(y, lastByTarget.get(el));
        lastByTarget.set(el, y);
      };
      document.addEventListener("scroll", onScroll, {
        capture: true,
        passive: true,
      });
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => {
        document.removeEventListener("scroll", onScroll, {
          capture: true,
        } as AddEventListenerOptions);
        window.removeEventListener("scroll", onScroll);
      };
    }, [expanded, isMobile]);

    // Ref on the strip's root so we can locate the scroll container above.
    const stripRef = useRef<HTMLDivElement | null>(null);

    const reload = useCallback(async () => {
      if (!spruceEntityId) {
        setDetails([]);
        return;
      }
      try {
        const res = await patientDetailsApi.getPatient(spruceEntityId);
        let baseDetails: Detail[] = [];
        if (res.data?.success && res.data?.data) {
          baseDetails = res.data.data.details || [];
        }

        // Also try to fetch  data as fallback
        if (patientName || patientEmail || patientPhone) {
          try {
            const pfRes = await practiceFusionApi.lookup({
              name: patientName,
              email: patientEmail,
              phone: patientPhone,
            });
            if (pfRes.data?.success && pfRes.data?.data) {
              const pf = pfRes.data.data;
              const pfEntries: Detail[] = [];
              if (pf.allergies)
                pfEntries.push({
                  category: "allergies",
                  content: pf.allergies,
                });
              if (pf.currentMedications)
                pfEntries.push({
                  category: "current_medications",
                  content: pf.currentMedications,
                });
              if (pf.primaryDiagnosis)
                pfEntries.push({
                  category: "primary_diagnosis",
                  content: pf.primaryDiagnosis,
                });
              if (pf.familyHistory)
                pfEntries.push({
                  category: "family_history",
                  content: pf.familyHistory,
                });
              // Only use PF data as fallback where patientDetails has no entry
              const existingCats = new Set(baseDetails.map((d) => d.category));
              pfEntries.forEach((pfEntry) => {
                if (!existingCats.has(pfEntry.category)) {
                  baseDetails.push(pfEntry);
                }
              });
            }
          } catch {
            // PF lookup failed silently
          }
        }

        setDetails(baseDetails);
      } catch {
        setErrorMsg("Failed to load patient clinical data.");
      }
    }, [spruceEntityId, patientName, patientEmail, patientPhone]);

    useEffect(() => {
      reload();
    }, [reload]);

    // Encounters / visit notes (read-only) for the Encounters pill.
    useEffect(() => {
      let cancelled = false;
      if (!spruceEntityId) {
        setEncounters([]);
        return;
      }
      setEncountersLoading(true);
      clinicalApi
        .listEncounters(spruceEntityId)
        .then((res) => {
          if (!cancelled) setEncounters(res.data?.data || []);
        })
        .catch(() => {
          if (!cancelled) setEncounters([]);
        })
        .finally(() => {
          if (!cancelled) setEncountersLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }, [spruceEntityId]);

    const latestDetails = useMemo(() => {
      const map: Record<string, Detail> = {};
      details.forEach((d) => {
        const cat = d.category || "";
        if (!cat) return;
        const createdAt = d.createdAt || d.created_at || "";
        if (
          !map[cat] ||
          (createdAt &&
            createdAt > (map[cat].createdAt || map[cat].created_at || ""))
        ) {
          map[cat] = d;
        }
      });
      return map;
    }, [details]);

    const allergies = latestDetails["allergies"]?.content || "";
    const textSectionValues: Record<TextSection, string> = {
      primary_dx: latestDetails["primary_diagnosis"]?.content || "",
      current_meds: latestDetails["current_medications"]?.content || "",
      family_history: latestDetails["family_history"]?.content || "",
    };
    const vitalHeight = latestDetails["vitals_height"]?.content || "";
    const vitalWeight = latestDetails["vitals_weight"]?.content || "";
    const vitalBp = latestDetails["vitals_bp"]?.content || "";
    const vitalSmoking = latestDetails["vitals_smoking"]?.content || "";
    const vitalPulse = latestDetails["vitals_pulse"]?.content || "";
    const vitalTemp = latestDetails["vitals_temp"]?.content || "";
    const vitalResp = latestDetails["vitals_resp"]?.content || "";
    const vitalSpo2 = latestDetails["vitals_spo2"]?.content || "";
    const vitalPain = latestDetails["vitals_pain"]?.content || "";
    const vitalBmi = useMemo(() => {
      const h = parseFloat(vitalHeight);
      const w = parseFloat(vitalWeight);
      if (!h || !w || h <= 0) return "";
      const bmi = w / ((h / 100) * (h / 100));
      return Number.isFinite(bmi) ? bmi.toFixed(1) : "";
    }, [vitalHeight, vitalWeight]);

    const allergiesEmpty = !allergies.trim();
    const allergiesNone =
      /^(none|nkda|nkfa|no known(?: drug| food)? allergies?|n\/a|na)\.?$/i.test(
        allergies.trim(),
      );
    const allergyHasData = !allergiesEmpty && !allergiesNone;
    const allergySevere = allergyHasData && SEVERE_RX.test(allergies);

    // Unit system (presentation only; storage stays metric)
    const [unitSystem, setUnitSystem] = useState<"metric" | "imperial">(() => {
      try {
        return (localStorage.getItem("vitalsUnitSystem") as
          | "metric"
          | "imperial") === "imperial"
          ? "imperial"
          : "metric";
      } catch {
        return "metric";
      }
    });
    useEffect(() => {
      try {
        localStorage.setItem("vitalsUnitSystem", unitSystem);
      } catch {
        /* noop */
      }
      // Notify same-tab listeners (the native 'storage' event only fires
      // across tabs, so the timeline in PatientDetailsView needs this.)
      try {
        window.dispatchEvent(new Event("vitalsUnitSystemChange"));
      } catch {
        /* noop */
      }
    }, [unitSystem]);

    const cmToInches = (cm: number) => cm / 2.54;
    const inchesToCm = (inches: number) => inches * 2.54;
    const kgToLb = (kg: number) => kg * 2.20462;
    const lbToKg = (lb: number) => lb / 2.20462;
    const cToF = (c: number) => (c * 9) / 5 + 32;
    const fToC = (f: number) => ((f - 32) * 5) / 9;

    const displayHeight = useMemo(() => {
      const cm = parseFloat(vitalHeight);
      if (!cm) return "";
      if (unitSystem === "metric") return `${cm} cm`;
      const totalIn = cmToInches(cm);
      const ft = Math.floor(totalIn / 12);
      const inch = Math.round(totalIn - ft * 12);
      return `${ft}′ ${inch}″`;
    }, [vitalHeight, unitSystem]);
    const displayWeight = useMemo(() => {
      const kg = parseFloat(vitalWeight);
      if (!kg) return "";
      if (unitSystem === "metric") return `${kg} kg`;
      return `${kgToLb(kg).toFixed(1)} lb`;
    }, [vitalWeight, unitSystem]);
    const displayTemp = useMemo(() => {
      const c = parseFloat(vitalTemp);
      if (!c && c !== 0) return "";
      if (unitSystem === "metric") return `${c.toFixed(1)} °C`;
      return `${cToF(c).toFixed(1)} °F`;
    }, [vitalTemp, unitSystem]);

    const dateFmtFull = useMemo(
      () =>
        new Intl.DateTimeFormat(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        }),
      [],
    );
    const formatVitalAge = useCallback(
      (iso?: string) => {
        if (!iso) return null;
        const d = new Date(iso);
        if (isNaN(d.getTime())) return null;
        const diffMs = Date.now() - d.getTime();
        const mins = Math.floor(diffMs / 60000);
        const hours = Math.floor(mins / 60);
        const days = Math.floor(hours / 24);
        let rel: string;
        if (mins < 1) rel = "just now";
        else if (mins < 60) rel = `${mins} min${mins === 1 ? "" : "s"} ago`;
        else if (hours < 24) rel = `${hours} hr${hours === 1 ? "" : "s"} ago`;
        else if (days < 30) rel = `${days} day${days === 1 ? "" : "s"} ago`;
        else if (days < 365) rel = `${Math.floor(days / 30)} mo ago`;
        else rel = `${Math.floor(days / 365)} yr ago`;
        const ONE_DAY_MS = 86400000;
        const color: "success.main" | "text.secondary" | "warning.main" =
          diffMs < ONE_DAY_MS
            ? "success.main"
            : diffMs > 7 * ONE_DAY_MS
              ? "warning.main"
              : "text.secondary";
        return { rel, absolute: dateFmtFull.format(d), color };
      },
      [dateFmtFull],
    );

    const vitalsMeta = (cat: string) =>
      formatVitalAge(
        latestDetails[cat]?.createdAt || latestDetails[cat]?.created_at,
      );

    // Renders "Last edited <relative> · <source>" caption, or "No entries yet"
    // when the category has never been saved for this patient. Used at the
    // bottom of each expanded chart panel for at-a-glance freshness.
    const renderLastEditedCaption = (detail?: Detail) => {
      const meta = detail
        ? formatVitalAge(detail.createdAt || detail.created_at)
        : null;
      if (!meta) {
        return (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 1,
              color: "text.disabled",
              fontStyle: "italic",
              fontSize: "0.7rem",
            }}
          >
            No entries yet
          </Typography>
        );
      }
      // Prefer the actual clinician's name stamped at save time. Legacy rows
      // stored the literal string "manual" before per-user attribution existed —
      // hide that token so the caption reads "Last edited 2 hr ago" instead of
      // the misleading "... · manual".
      const rawAuthor = (detail?.createdBy || detail?.created_by || "").trim();
      const author =
        rawAuthor && rawAuthor.toLowerCase() !== "manual" ? rawAuthor : "";
      const label = author
        ? `Last edited ${meta.rel} by ${author}`
        : `Last edited ${meta.rel}`;
      const ariaLabel = author
        ? `Last edited ${meta.absolute} by ${author}`
        : `Last edited ${meta.absolute}`;
      return (
        <Tooltip title={meta.absolute} placement="top">
          <Box
            component="span"
            tabIndex={0}
            aria-label={ariaLabel}
            sx={{
              display: "block",
              mt: 1,
              color: meta.color,
              fontSize: "0.7rem",
              cursor: "help",
              outline: "none",
              "&:focus-visible": { textDecoration: "underline" },
            }}
          >
            {label}
          </Box>
        </Tooltip>
      );
    };

    const latestVitalDetail = useMemo<Detail | undefined>(() => {
      const cats = [
        "vitals_height",
        "vitals_weight",
        "vitals_bp",
        "vitals_smoking",
        "vitals_pulse",
        "vitals_temp",
        "vitals_resp",
        "vitals_spo2",
        "vitals_pain",
      ];
      let latest: Detail | undefined;
      let latestIso = "";
      cats.forEach((c) => {
        const d = latestDetails[c];
        const iso = d?.createdAt || d?.created_at || "";
        if (iso && iso > latestIso) {
          latestIso = iso;
          latest = d;
        }
      });
      return latest;
    }, [latestDetails]);
    const latestVitalMeta = useMemo(
      () =>
        formatVitalAge(
          latestVitalDetail?.createdAt || latestVitalDetail?.created_at,
        ),
      [latestVitalDetail, formatVitalAge],
    );

    // Allergies inline edit
    const [allergyEditing, setAllergyEditing] = useState(false);
    const [allergyDraft, setAllergyDraft] = useState("");
    const [savingAllergies, setSavingAllergies] = useState(false);
    useEffect(() => {
      setAllergyEditing(false);
    }, [spruceEntityId, resetSignal]);
    // Mobile chart editor auto-enters edit mode the moment the full-screen
    // Dialog opens for a section — the chevron mental model is "tap to edit",
    // and the empty read-only state inside a full-screen sheet feels broken.
    // Desktop keeps the click-to-Edit affordance.
    useEffect(() => {
      if (!isMobile || !expanded) return;
      if (expanded === "allergies") {
        setAllergyDraft(allergies);
        setAllergyEditing(true);
      } else if (
        expanded === "primary_dx" ||
        expanded === "current_meds" ||
        expanded === "family_history"
      ) {
        setTextDrafts((prev) => ({
          ...prev,
          [expanded]: textSectionValues[expanded],
        }));
        setTextEditing((prev) => ({ ...prev, [expanded]: true }));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expanded, isMobile]);
    // Cross-breakpoint guard: vitals is mobile-routed straight to its own modal,
    // so if the viewport flips from desktop (where the user opened the inline
    // vitals Collapse) to mobile while `expanded === 'vitals'`, the new mobile
    // text editor would render with no valid save path. Hand off cleanly.
    useEffect(() => {
      if (isMobile && expanded === "vitals") {
        setExpanded(null);
        openVitals();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile, expanded]);
    // Centralized close used by every mobile editor exit (back arrow, Cancel,
    // backdrop, Esc). Resets shared edit/draft flags so unsaved mobile state
    // never bleeds into the desktop inline panels after a breakpoint flip.
    const closeMobileEditor = useCallback(() => {
      if (expanded === "allergies") {
        setAllergyEditing(false);
        setAllergyDraft("");
      } else if (
        expanded === "primary_dx" ||
        expanded === "current_meds" ||
        expanded === "family_history"
      ) {
        const sec = expanded;
        setTextEditing((prev) => ({ ...prev, [sec]: false }));
        setTextDrafts((prev) => ({ ...prev, [sec]: "" }));
      }
      setExpanded(null);
    }, [expanded]);
    const startAllergyEdit = () => {
      setAllergyDraft(allergies);
      setAllergyEditing(true);
    };
    const saveAllergies = async () => {
      if (!spruceEntityId) return;
      setSavingAllergies(true);
      try {
        await patientDetailsApi.addDetail(
          spruceEntityId,
          "allergies",
          allergyDraft.trim(),
        );
        await reload();
        notifyChartChange(spruceEntityId);
        setAllergyEditing(false);
      } catch {
        setErrorMsg("Failed to save allergies.");
      } finally {
        setSavingAllergies(false);
      }
    };

    // Text-section (Primary DX / Current Meds / Family History) inline edit
    const [textEditing, setTextEditing] = useState<
      Record<TextSection, boolean>
    >({
      primary_dx: false,
      current_meds: false,
      family_history: false,
    });
    // Keep the ref the scroll-auto-collapse effect reads in sync with
    // the current editing state so saves/cancels immediately re-arm or
    // disarm the gate without needing to re-attach the listener.
    anyTextEditingRef.current =
      textEditing.primary_dx ||
      textEditing.current_meds ||
      textEditing.family_history;
    const [textDrafts, setTextDrafts] = useState<Record<TextSection, string>>({
      primary_dx: "",
      current_meds: "",
      family_history: "",
    });
    const [savingSection, setSavingSection] = useState<TextSection | null>(
      null,
    );
    useEffect(() => {
      setTextEditing({
        primary_dx: false,
        current_meds: false,
        family_history: false,
      });
    }, [spruceEntityId, resetSignal]);
    const startTextEdit = (section: TextSection) => {
      setTextDrafts((prev) => ({
        ...prev,
        [section]: textSectionValues[section],
      }));
      setTextEditing((prev) => ({ ...prev, [section]: true }));
    };
    const cancelTextEdit = (section: TextSection) => {
      setTextEditing((prev) => ({ ...prev, [section]: false }));
    };
    const saveTextSection = async (section: TextSection) => {
      if (!spruceEntityId) return;
      setSavingSection(section);
      try {
        await patientDetailsApi.addDetail(
          spruceEntityId,
          TEXT_SECTION_CONFIG[section].category,
          textDrafts[section].trim(),
        );
        await reload();
        notifyChartChange(spruceEntityId);
        setTextEditing((prev) => ({ ...prev, [section]: false }));
      } catch {
        setErrorMsg(`Failed to save ${TEXT_SECTION_CONFIG[section].label}.`);
      } finally {
        setSavingSection(null);
      }
    };

    // Vitals edit dialog (self-contained)
    const [vitalsOpen, setVitalsOpen] = useState(false);
    const [vitalsDraft, setVitalsDraft] = useState<Record<VitalField, string>>({
      height: "",
      weight: "",
      bp: "",
      smoking: "",
      pulse: "",
      temp: "",
      resp: "",
      spo2: "",
      pain: "",
    });
    const [vitalsDirty, setVitalsDirty] = useState<Set<VitalField>>(new Set());
    const [savingVitals, setSavingVitals] = useState(false);

    const updateDraft = (field: VitalField, val: string) => {
      setVitalsDraft((prev) => ({ ...prev, [field]: val }));
      setVitalsDirty((prev) => {
        const next = new Set(prev);
        next.add(field);
        return next;
      });
    };

    const draftFromCanonical = useCallback(
      (sys: "metric" | "imperial") => ({
        height: vitalHeight
          ? sys === "metric"
            ? vitalHeight
            : cmToInches(parseFloat(vitalHeight)).toFixed(1)
          : "",
        weight: vitalWeight
          ? sys === "metric"
            ? vitalWeight
            : kgToLb(parseFloat(vitalWeight)).toFixed(1)
          : "",
        bp: vitalBp,
        smoking: vitalSmoking,
        pulse: vitalPulse,
        temp: vitalTemp
          ? sys === "metric"
            ? vitalTemp
            : cToF(parseFloat(vitalTemp)).toFixed(1)
          : "",
        resp: vitalResp,
        spo2: vitalSpo2,
        pain: vitalPain,
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }),
      [
        vitalHeight,
        vitalWeight,
        vitalBp,
        vitalSmoking,
        vitalPulse,
        vitalTemp,
        vitalResp,
        vitalSpo2,
        vitalPain,
      ],
    );

    const openVitals = () => {
      setVitalsDraft(draftFromCanonical(unitSystem));
      setVitalsDirty(new Set());
      setVitalsOpen(true);
    };

    const prevUnitRef = useRef(unitSystem);
    useEffect(() => {
      if (!vitalsOpen) {
        prevUnitRef.current = unitSystem;
        return;
      }
      const prev = prevUnitRef.current;
      if (prev === unitSystem) return;
      setVitalsDraft((d) => {
        const next = { ...d };
        if (d.height) {
          const n = parseFloat(d.height);
          if (!isNaN(n))
            next.height =
              unitSystem === "metric"
                ? inchesToCm(n).toFixed(1)
                : cmToInches(n).toFixed(1);
        }
        if (d.weight) {
          const n = parseFloat(d.weight);
          if (!isNaN(n))
            next.weight =
              unitSystem === "metric"
                ? lbToKg(n).toFixed(2)
                : kgToLb(n).toFixed(1);
        }
        if (d.temp) {
          const n = parseFloat(d.temp);
          if (!isNaN(n))
            next.temp =
              unitSystem === "metric" ? fToC(n).toFixed(1) : cToF(n).toFixed(1);
        }
        return next;
      });
      prevUnitRef.current = unitSystem;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitSystem, vitalsOpen]);

    const saveVitals = async () => {
      if (!spruceEntityId) return;
      setSavingVitals(true);
      try {
        const toCanonical = (field: VitalField, value: string): string => {
          const v = value.trim();
          if (!v) return "";
          if (field === "height" || field === "weight" || field === "temp") {
            const n = parseFloat(v);
            if (isNaN(n)) return "";
            if (unitSystem === "metric") return v;
            if (field === "height") return inchesToCm(n).toFixed(1);
            if (field === "weight") return lbToKg(n).toFixed(2);
            return fToC(n).toFixed(1);
          }
          return v;
        };
        const fieldToCategory: Record<VitalField, string> = {
          height: "vitals_height",
          weight: "vitals_weight",
          bp: "vitals_bp",
          smoking: "vitals_smoking",
          pulse: "vitals_pulse",
          temp: "vitals_temp",
          resp: "vitals_resp",
          spo2: "vitals_spo2",
          pain: "vitals_pain",
        };
        const tasks: Promise<any>[] = [];
        vitalsDirty.forEach((field) => {
          const canonical = toCanonical(field, vitalsDraft[field]);
          tasks.push(
            patientDetailsApi.addDetail(
              spruceEntityId,
              fieldToCategory[field],
              canonical,
            ),
          );
        });
        // Auto-store BMI historically whenever height or weight changes, so it
        // appears in the chart-edits timeline like every other vital. BMI is
        // unit-agnostic (derived from canonical cm + kg).
        if (vitalsDirty.has("height") || vitalsDirty.has("weight")) {
          const hCm = parseFloat(toCanonical("height", vitalsDraft.height));
          const wKg = parseFloat(toCanonical("weight", vitalsDraft.weight));
          if (Number.isFinite(hCm) && hCm > 0 && Number.isFinite(wKg) && wKg > 0) {
            const bmi = wKg / ((hCm / 100) * (hCm / 100));
            if (Number.isFinite(bmi)) {
              tasks.push(
                patientDetailsApi.addDetail(spruceEntityId, "vitals_bmi", bmi.toFixed(1)),
              );
            }
          }
        }
        if (tasks.length === 0) {
          setVitalsOpen(false);
          return;
        }
        await Promise.all(tasks);
        await reload();
        notifyChartChange(spruceEntityId);
        setVitalsOpen(false);
      } catch {
        setErrorMsg("Failed to save vitals.");
      } finally {
        setSavingVitals(false);
      }
    };

    // Per-field edit history dialog. `historyCategory` is the patient_details
    // category (e.g. "allergies", "vitals_bp") whose full revision history is
    // open; `historyTitle` is the human label shown in the dialog header.
    const [historyCategory, setHistoryCategory] = useState<string | null>(null);
    // Per-row expand state for long revisions in the Edit History dialog.
    // Keyed by entry id (or iso fallback) so toggling one row doesn't expand
    // every row. Cleared whenever the dialog closes so each open starts in
    // the compact, scannable default.
    const [expandedHistoryRows, setExpandedHistoryRows] = useState<Set<string>>(
      new Set(),
    );
    const toggleHistoryRow = (key: string) => {
      setExpandedHistoryRows((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    };
    const [historyTitle, setHistoryTitle] = useState<string>("");
    const openHistory = (category: string, title: string) => {
      setHistoryCategory(category);
      setHistoryTitle(title);
    };
    const closeHistory = () => {
      setHistoryCategory(null);
      setHistoryTitle("");
      setExpandedHistoryRows(new Set());
    };

    // Per-category revision counts, computed once per `details` change so the
    // history icons (which need both a disabled state and a count tooltip) don't
    // re-scan the array per render per cell.
    const categoryCounts = useMemo<Record<string, number>>(() => {
      const out: Record<string, number> = {};
      details.forEach((d) => {
        const c = d.category || "";
        if (!c) return;
        out[c] = (out[c] || 0) + 1;
      });
      return out;
    }, [details]);

    // All revisions for the open category, newest first. The /patient details
    // payload already includes every patient_details row with created_by +
    // created_by_user_id, so no extra fetch is needed.
    const historyEntries = useMemo<Detail[]>(() => {
      if (!historyCategory) return [];
      return details
        .filter((d) => d.category === historyCategory)
        .slice()
        .sort((a, b) => {
          const aIso = a.createdAt || a.created_at || "";
          const bIso = b.createdAt || b.created_at || "";
          return aIso < bIso ? 1 : aIso > bIso ? -1 : 0;
        });
    }, [details, historyCategory]);

    // Render the saved value for a history row, plus a compact line-by-line
    // before/after diff against the immediately-previous (older) revision when
    // one exists. Lines added in this revision are green, removed lines are red.
    const renderHistoryValue = (
      entry: Detail,
      prev?: Detail,
      rowKey?: string,
    ) => {
      const current = (entry.content || "").trim();
      const previous = (prev?.content || "").trim();
      const toLines = (s: string) =>
        s
          .split(/\r?\n|,|;/)
          .map((x) => x.trim())
          .filter(Boolean);
      const curLines = toLines(current);
      const prevLines = toLines(previous);
      const prevSet = new Set(prevLines.map((l) => l.toLowerCase()));
      const curSet = new Set(curLines.map((l) => l.toLowerCase()));
      const added = curLines.filter((l) => !prevSet.has(l.toLowerCase()));
      const removed = prevLines.filter((l) => !curSet.has(l.toLowerCase()));
      const hasDiff = prev && (added.length > 0 || removed.length > 0);
      // Collapse long revisions to a 6-line preview with a Show more / Show
      // less toggle so the dialog stays scannable when someone pasted a
      // long med list or family history paragraph. Only show the toggle
      // when the content would actually be clipped — short values render
      // identically to before.
      const COLLAPSE_LINE_LIMIT = 2;
      const COLLAPSE_CHAR_LIMIT = 80;
      const newlineCount = (current.match(/\n/g) || []).length + 1;
      const isLong =
        newlineCount > COLLAPSE_LINE_LIMIT ||
        current.length > COLLAPSE_CHAR_LIMIT;
      const expanded = rowKey ? expandedHistoryRows.has(rowKey) : true;
      const showToggle = isLong && !!rowKey;
      const clampSx =
        showToggle && !expanded
          ? {
              display: "-webkit-box",
              WebkitLineClamp: COLLAPSE_LINE_LIMIT,
              WebkitBoxOrient: "vertical" as const,
              overflow: "hidden",
            }
          : {};

      return (
        <Box>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              color: current ? "text.primary" : "text.disabled",
              fontStyle: current ? "normal" : "italic",
              fontWeight: 500,
              ...clampSx,
            }}
          >
            {current || "(empty)"}
          </Typography>
          {showToggle && (
            <Button
              size="small"
              variant="text"
              onClick={() => rowKey && toggleHistoryRow(rowKey)}
              sx={{
                mt: 0.25,
                px: 0.5,
                py: 0,
                minWidth: 0,
                textTransform: "none",
                fontSize: "0.72rem",
                fontWeight: 600,
              }}
            >
              {expanded ? "Show less" : "Show more"}
            </Button>
          )}
          {hasDiff && (
            <Box
              sx={{
                mt: 0.75,
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
              }}
            >
              {added.map((l, i) => (
                <Typography
                  key={`a-${i}`}
                  variant="caption"
                  sx={{
                    color: "success.dark",
                    fontFamily: "monospace",
                    fontSize: "0.72rem",
                  }}
                >
                  + {l}
                </Typography>
              ))}
              {removed.map((l, i) => (
                <Typography
                  key={`r-${i}`}
                  variant="caption"
                  sx={{
                    color: "error.dark",
                    fontFamily: "monospace",
                    fontSize: "0.72rem",
                  }}
                >
                  − {l}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      );
    };

    // Small clock-icon button placed next to "Edit" on each chart panel /
    // vitals cell. Disabled when the field has no entries to show.
    const renderHistoryButton = (
      category: string,
      title: string,
      opts: { dense?: boolean } = {},
    ) => {
      const count = categoryCounts[category] || 0;
      const disabled = count === 0;
      const { dense } = opts;
      return (
        <Tooltip
          title={
            disabled ? "No edit history yet" : `View edit history (${count})`
          }
        >
          <span>
            <IconButton
              size="small"
              aria-label={`View edit history for ${title}`}
              onClick={(e) => {
                e.stopPropagation();
                openHistory(category, title);
              }}
              disabled={disabled}
              sx={{
                p: dense ? 0.25 : undefined,
                color: disabled ? "text.disabled" : "text.secondary",
              }}
            >
              <HistoryIcon
                sx={{ fontSize: dense ? "0.95rem" : undefined }}
                fontSize={dense ? undefined : "small"}
              />
            </IconButton>
          </span>
        </Tooltip>
      );
    };

    if (!spruceEntityId) return null;

    const allergyOpen = expanded === "allergies";
    const vitalsExpanded = expanded === "vitals";
    const toggle = (which: ExpandedKey) =>
      setExpanded((prev) => (prev === which ? null : which));
    const allergyTone: "neutral" | "warning" | "error" =
      allergiesEmpty || allergiesNone
        ? "neutral"
        : allergySevere
          ? "error"
          : "warning";
    const allergyLabel = "Allergies";
    const vitalsLabel = "Vitals";

    // Per-category accent palette. Each pill is white by default with a colored
    // border + colored text, picking up a subtle tinted background once the
    // section has data. Severe allergies override to a solid red fill so the
    // safety signal can't be missed.
    type PillTone = {
      accent: string; // 600-weight color for border/text/dot
      deep: string; // 700/800 weight for hover & open text
      tint: string; // 50-weight wash for filled background
      soft: string; // 100-weight wash for hover/open background
    };
    const PILL_TONES: Record<ExpandedKey, PillTone> = {
      allergies: {
        accent: "#dc2626",
        deep: "#991b1b",
        tint: "#fef2f2",
        soft: "#fee2e2",
      }, // red
      vitals: {
        accent: "#0d9488",
        deep: "#115e59",
        tint: "#f0fdfa",
        soft: "#ccfbf1",
      }, // teal
      primary_dx: {
        accent: "#4f46e5",
        deep: "#3730a3",
        tint: "#eef2ff",
        soft: "#e0e7ff",
      }, // indigo
      current_meds: {
        accent: "#d97706",
        deep: "#92400e",
        tint: "#fffbeb",
        soft: "#fef3c7",
      }, // amber
      family_history: {
        accent: "#059669",
        deep: "#065f46",
        tint: "#ecfdf5",
        soft: "#d1fae5",
      }, // emerald
      encounters: {
        accent: "#0284c7",
        deep: "#075985",
        tint: "#f0f9ff",
        soft: "#e0f2fe",
      }, // sky
    };

    type PillProps = {
      keyId: ExpandedKey;
      label: string;
      hasData: boolean;
      isOpen: boolean;
      onClick: () => void;
      /** When true, override styling to a loud filled state (severe allergy). */
      alarm?: boolean;
      ariaLabel?: string;
    };

    const renderPill = ({
      keyId,
      label,
      hasData,
      isOpen,
      onClick,
      alarm,
      ariaLabel,
    }: PillProps) => {
      const tone = PILL_TONES[keyId];
      const filled = hasData && !alarm;
      const empty = !hasData;
      const baseSx = {
        textTransform: "none" as const,
        borderRadius: 999,
        px: 1.5,
        py: 0.5,
        fontWeight: 600,
        letterSpacing: 0.2,
        fontSize: "0.8125rem",
        minHeight: 32,
        lineHeight: 1.4,
        border: "1.5px solid",
        transition: "all .15s ease",
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
      };
      // Three visual modes: alarm (solid red), filled (white w/ tinted hover),
      // empty (white w/ dashed border). Open state amps up the background.
      const modeSx = alarm
        ? {
            bgcolor: tone.accent,
            color: "#fff",
            borderColor: tone.accent,
            boxShadow: `0 0 0 3px ${tone.tint}`,
            "&:hover": {
              bgcolor: tone.deep,
              borderColor: tone.deep,
              boxShadow: `0 0 0 3px ${tone.soft}`,
            },
          }
        : filled
          ? {
              bgcolor: isOpen ? tone.soft : "#ffffff",
              color: tone.deep,
              borderColor: tone.accent,
              "&:hover": { bgcolor: tone.tint, borderColor: tone.deep },
            }
          : {
              bgcolor: isOpen ? tone.tint : "#ffffff",
              color: "text.secondary",
              borderColor: "grey.300",
              borderStyle: "dashed",
              "&:hover": {
                bgcolor: "grey.50",
                borderColor: tone.accent,
                color: tone.accent,
              },
            };
      return (
        <Button
          key={keyId}
          size="small"
          onClick={onClick}
          disableElevation
          disableRipple={false}
          aria-expanded={isOpen}
          aria-label={ariaLabel || label}
          endIcon={
            <ExpandMoreIcon
              sx={{
                fontSize: "1rem !important",
                transform: isOpen ? "rotate(180deg)" : "none",
                transition: "transform .15s",
                color: "currentColor",
                opacity: alarm ? 1 : 0.7,
              }}
            />
          }
          sx={{ ...baseSx, ...modeSx }}
        >
          {/* Status dot — filled for has-data, hollow ring for empty. Hidden in
            alarm mode since the whole pill is already loud. */}
          {!alarm && (
            <Box
              component="span"
              aria-hidden
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                flexShrink: 0,
                bgcolor: empty ? "transparent" : tone.accent,
                border: empty ? `1.5px solid ${tone.accent}` : "none",
                opacity: empty ? 0.6 : 1,
              }}
            />
          )}
          {alarm && (
            <WarningAmberIcon sx={{ fontSize: "0.95rem", color: "inherit" }} />
          )}
          <span>{label}</span>
        </Button>
      );
    };

    const renderTextPill = (section: TextSection) => {
      const cfg = TEXT_SECTION_CONFIG[section];
      const hasData = !!textSectionValues[section].trim();
      return renderPill({
        keyId: section,
        label: cfg.label,
        hasData,
        isOpen: expanded === section,
        onClick: () => toggle(section),
      });
    };

    const renderTextPanel = (section: TextSection) => {
      const cfg = TEXT_SECTION_CONFIG[section];
      const isOpen = expanded === section;
      // Primary DX is backed by the structured diagnoses store (ICD-10),
      // shared live with the Problems & Diagnoses panel on the Patient Details
      // tab. Add/edit here updates there and vice-versa.
      if (section === "primary_dx") {
        return (
          <Collapse key={section} in={isOpen} unmountOnExit>
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                borderLeft: "4px solid",
                borderColor: "primary.main",
                bgcolor: "primary.50",
                borderRadius: 1,
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    fontWeight: 700,
                    color: "primary.dark",
                    display: "block",
                    mb: 0.75,
                  }}
                >
                  {cfg.label}
                </Typography>
                <PatientDiagnosisManager spruceEntityId={spruceEntityId} />
              </Box>
              <Tooltip title="Close">
                <IconButton
                  size="small"
                  aria-label={`Collapse ${cfg.label}`}
                  onClick={() => setExpanded(null)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Collapse>
        );
      }
      // Current Meds is backed by the structured detail list (one row per
      // medication), shared live with the Patient Details tab and the mobile
      // sheet — so the full med list shows here, not just the latest entry.
      if (section === "current_meds") {
        return (
          <Collapse key={section} in={isOpen} unmountOnExit>
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                borderLeft: "4px solid",
                borderColor: "warning.main",
                bgcolor: "warning.50",
                borderRadius: 1,
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <PatientDetailListManager
                  spruceEntityId={spruceEntityId}
                  category="current_medications"
                  label={cfg.label}
                  placeholder={cfg.placeholder}
                  emptyText="No current medications recorded"
                  secondaryLabel="Directions"
                />
              </Box>
              <Tooltip title="Close">
                <IconButton
                  size="small"
                  aria-label={`Collapse ${cfg.label}`}
                  onClick={() => setExpanded(null)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Collapse>
        );
      }
      const value = textSectionValues[section];
      const editing = textEditing[section];
      const saving = savingSection === section;
      return (
        <Collapse key={section} in={isOpen} unmountOnExit>
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              borderLeft: "4px solid",
              borderColor: value.trim() ? "primary.main" : "divider",
              bgcolor: value.trim() ? "primary.50" : "grey.50",
              borderRadius: 1,
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    fontWeight: 700,
                    color: value.trim() ? "primary.dark" : "text.secondary",
                  }}
                >
                  {cfg.label}
                </Typography>
                {!editing && (
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
                  >
                    {renderHistoryButton(cfg.category, cfg.label)}
                    <Button
                      size="small"
                      startIcon={<EditIcon fontSize="small" />}
                      onClick={() => startTextEdit(section)}
                      sx={{ textTransform: "none", minWidth: 0, py: 0 }}
                    >
                      Edit
                    </Button>
                  </Box>
                )}
              </Box>
              {editing ? (
                <Box sx={{ mt: 0.5 }}>
                  <TextField
                    value={textDrafts[section]}
                    onChange={(e) =>
                      setTextDrafts((prev) => ({
                        ...prev,
                        [section]: e.target.value,
                      }))
                    }
                    multiline
                    minRows={2}
                    maxRows={8}
                    fullWidth
                    size="small"
                    placeholder={cfg.placeholder}
                    autoFocus
                    sx={{ bgcolor: "background.paper" }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    <Button
                      size="small"
                      onClick={() => cancelTextEdit(section)}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<SaveIcon fontSize="small" />}
                      onClick={() => saveTextSection(section)}
                      disabled={saving}
                    >
                      {saving ? "Saving…" : "Save"}
                    </Button>
                  </Box>
                </Box>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: value.trim() ? "text.primary" : "text.secondary",
                      fontStyle: value.trim() ? "normal" : "italic",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      mt: 0.25,
                    }}
                  >
                    {value.trim() ||
                      `Not documented — click Edit to add ${cfg.label}.`}
                  </Typography>
                  {renderLastEditedCaption(latestDetails[cfg.category])}
                </>
              )}
            </Box>
            <Tooltip title="Close">
              <IconButton
                size="small"
                aria-label={`Collapse ${cfg.label}`}
                onClick={() => setExpanded(null)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Collapse>
      );
    };

    return (
      <Paper
        ref={stripRef}
        square
        elevation={0}
        sx={{
          // A chart-surface host (desktop) or a trigger-hidden mobile mount
          // with nothing expanded renders only the portal sheet — collapse to
          // zero height so it takes no layout space.
          ...(chartSurface || (isMobile && hideMobileTrigger && !expanded)
            ? { p: 0, border: 0 }
            : {
                px: { xs: 1.5, sm: 2, md: 3 },
                py: 1,
                borderBottom: 1,
                borderColor: "divider",
              }),
          bgcolor: "background.paper",
          display: "flex",
          flexDirection: "column",
          gap: 0,
        }}
      >
        {chartSurface ? null : isMobile ? (
          hideMobileTrigger ? null : (() => {
            // Compact mobile entry point: a single "Chart" button that opens a
            // bottom sheet listing all five chart sections. The button itself
            // surfaces two safety signals at a glance so the doctor never has
            // to open the sheet to know:
            //  - red WarningAmber chip when allergies are severe
            //  - small "n filled" pill showing how many sections have data
            const filledCount =
              (allergyHasData || allergiesNone ? 1 : 0) +
              (latestVitalMeta ? 1 : 0) +
              (textSectionValues.primary_dx.trim() ? 1 : 0) +
              (textSectionValues.current_meds.trim() ? 1 : 0) +
              (encounters.length > 0 ? 1 : 0) +
              (textSectionValues.family_history.trim() ? 1 : 0);
            return (
              <Button
                fullWidth
                onClick={() => setChartSheetOpen(true)}
                disableElevation
                aria-haspopup="dialog"
                aria-expanded={chartSheetOpen}
                aria-controls="patient-chart-sheet"
                startIcon={
                  <MedicalInformationIcon
                    fontSize="small"
                    sx={{ color: allergySevere ? "#fff" : "primary.main" }}
                  />
                }
                endIcon={
                  <ExpandMoreIcon fontSize="small" sx={{ opacity: 0.7 }} />
                }
                sx={{
                  textTransform: "none",
                  justifyContent: "space-between",
                  borderRadius: 2,
                  py: 0.75,
                  px: 1.5,
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  border: "1px solid",
                  borderColor: allergySevere ? "#dc2626" : "divider",
                  bgcolor: allergySevere ? "#dc2626" : "background.paper",
                  color: allergySevere ? "#fff" : "text.primary",
                  boxShadow: allergySevere ? "0 0 0 3px #fef2f2" : "none",
                  "&:hover": {
                    bgcolor: allergySevere ? "#991b1b" : "grey.50",
                    borderColor: allergySevere ? "#991b1b" : "grey.400",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <span>Chart</span>
                  {allergySevere && (
                    <Box
                      component="span"
                      aria-label="Severe allergy on file"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.25,
                        fontSize: "0.7rem",
                        fontWeight: 800,
                        px: 0.75,
                        py: 0.125,
                        borderRadius: 999,
                        bgcolor: "#fff",
                        color: "#dc2626",
                        letterSpacing: 0.6,
                        textTransform: "uppercase",
                      }}
                    >
                      <WarningAmberIcon sx={{ fontSize: "0.85rem" }} />
                      Severe
                    </Box>
                  )}
                  <Box
                    component="span"
                    sx={{
                      ml: "auto",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: allergySevere
                        ? "rgba(255,255,255,0.85)"
                        : "text.secondary",
                    }}
                  >
                    {filledCount}/6 filled
                  </Box>
                </Box>
              </Button>
            );
          })()
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {/* Priority order (HX reorg): meds + allergies lead, Primary DX/ICD last. */}
            {renderTextPill("current_meds")}
            {renderPill({
              keyId: "allergies",
              label: allergyLabel,
              hasData: allergyHasData || allergiesNone,
              isOpen: allergyOpen,
              onClick: () => toggle("allergies"),
              alarm: allergySevere,
              ariaLabel: allergySevere
                ? `${allergyLabel} — severe`
                : allergyLabel,
            })}
            {renderPill({
              keyId: "vitals",
              label: vitalsLabel,
              hasData: !!latestVitalMeta,
              isOpen: vitalsExpanded,
              onClick: () => toggle("vitals"),
            })}
            {renderPill({
              keyId: "encounters",
              label: "Encounters",
              hasData: encounters.length > 0,
              isOpen: expanded === "encounters",
              onClick: () => toggle("encounters"),
            })}
            {renderTextPill("family_history")}
            {renderTextPill("primary_dx")}
          </Box>
        )}

        {/* Mobile chart bottom sheet — lists all five sections with previews.
          Tapping a row closes the sheet and opens that section's existing
          in-strip Collapse panel just below the Chart button. */}
        {(isMobile || chartSurface) &&
          (() => {
            type SheetRow = {
              key: ExpandedKey;
              label: string;
              icon: React.ReactNode;
              accent: string;
              deep: string;
              tint: string;
              preview: string;
              hasData: boolean;
              alarm?: boolean;
            };
            const ellipsize = (s: string, max = 60) => {
              const t = s.replace(/\s+/g, " ").trim();
              return t.length <= max ? t : t.slice(0, max - 1) + "…";
            };
            const vitalsPreviewParts = [
              vitalBp && `BP ${vitalBp}`,
              vitalPulse && `HR ${vitalPulse}`,
              displayWeight && `Wt ${displayWeight}`,
            ].filter(Boolean) as string[];
            // Priority order (HX reorg): meds + allergies lead, Primary DX/ICD last.
            const rows: SheetRow[] = [
              {
                key: "current_meds",
                label: "Current Meds",
                icon: <MedicationIcon fontSize="small" />,
                accent: PILL_TONES.current_meds.accent,
                deep: PILL_TONES.current_meds.deep,
                tint: PILL_TONES.current_meds.tint,
                preview: textSectionValues.current_meds.trim()
                  ? ellipsize(textSectionValues.current_meds)
                  : "Not documented",
                hasData: !!textSectionValues.current_meds.trim(),
              },
              {
                key: "allergies",
                label: "Allergies",
                icon: <WarningAmberIcon fontSize="small" />,
                accent: PILL_TONES.allergies.accent,
                deep: PILL_TONES.allergies.deep,
                tint: PILL_TONES.allergies.tint,
                preview: allergyHasData
                  ? ellipsize(allergies)
                  : allergiesNone
                    ? "No known allergies"
                    : "Not documented",
                hasData: allergyHasData || allergiesNone,
                alarm: allergySevere,
              },
              {
                key: "vitals",
                label: "Vitals",
                icon: <MonitorHeartIcon fontSize="small" />,
                accent: PILL_TONES.vitals.accent,
                deep: PILL_TONES.vitals.deep,
                tint: PILL_TONES.vitals.tint,
                preview: vitalsPreviewParts.length
                  ? vitalsPreviewParts.join(" · ")
                  : "Not documented",
                hasData: !!latestVitalMeta,
              },
              {
                key: "encounters",
                label: "Encounters",
                icon: <EventNoteIcon fontSize="small" />,
                accent: PILL_TONES.encounters.accent,
                deep: PILL_TONES.encounters.deep,
                tint: PILL_TONES.encounters.tint,
                preview: encounters.length
                  ? `${encounters.length} visit${encounters.length === 1 ? "" : "s"} on file`
                  : "No encounters",
                hasData: encounters.length > 0,
              },
              {
                key: "family_history",
                label: "Family History",
                icon: <FamilyRestroomIcon fontSize="small" />,
                accent: PILL_TONES.family_history.accent,
                deep: PILL_TONES.family_history.deep,
                tint: PILL_TONES.family_history.tint,
                preview: textSectionValues.family_history.trim()
                  ? ellipsize(textSectionValues.family_history)
                  : "Not documented",
                hasData: !!textSectionValues.family_history.trim(),
              },
              {
                key: "primary_dx",
                label: "Primary DX",
                icon: <AssignmentIcon fontSize="small" />,
                accent: PILL_TONES.primary_dx.accent,
                deep: PILL_TONES.primary_dx.deep,
                tint: PILL_TONES.primary_dx.tint,
                preview: textSectionValues.primary_dx.trim()
                  ? ellipsize(textSectionValues.primary_dx)
                  : "Not documented",
                hasData: !!textSectionValues.primary_dx.trim(),
              },
            ];
            return (
              <SwipeableDrawer
                anchor={chartSurface ? "right" : "bottom"}
                open={chartSheetOpen}
                onOpen={() => setChartSheetOpen(true)}
                onClose={() => setChartSheetOpen(false)}
                disableSwipeToOpen
                ModalProps={{ "aria-labelledby": "patient-chart-sheet-title" }}
                sx={drawerZIndex !== undefined ? { zIndex: drawerZIndex } : undefined}
                PaperProps={{
                  id: "patient-chart-sheet",
                  role: "dialog",
                  "aria-modal": true,
                  sx: chartSurface
                    ? {
                        // Desktop: right-side panel, full height.
                        width: { md: 460, lg: 520 },
                        maxWidth: "100vw",
                      }
                    : {
                        borderTopLeftRadius: 20,
                        borderTopRightRadius: 20,
                        maxHeight: "85vh",
                        pb: "max(env(safe-area-inset-bottom), 12px)",
                      },
                }}
              >
                {/* Drag handle — bottom sheet only */}
                {!chartSurface && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    pt: 1,
                    pb: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 4,
                      borderRadius: 2,
                      bgcolor: "grey.300",
                    }}
                  />
                </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1,
                  }}
                >
                  <Typography
                    id="patient-chart-sheet-title"
                    variant="subtitle1"
                    sx={{ fontWeight: 700 }}
                  >
                    Patient Chart
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Close chart"
                    onClick={() => setChartSheetOpen(false)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <Divider />
                <List sx={{ py: 0 }}>
                  {rows.map((row, i) => (
                    <React.Fragment key={row.key}>
                      {i > 0 && <Divider component="li" />}
                      <ListItemButton
                        onClick={() => {
                          setChartSheetOpen(false);
                          if (row.key === "vitals") {
                            openVitals();
                          } else {
                            setExpanded(row.key);
                          }
                        }}
                        sx={{
                          py: 1.25,
                          px: 2,
                          bgcolor: row.alarm ? "#fef2f2" : "transparent",
                          "&:hover": {
                            bgcolor: row.alarm ? "#fee2e2" : "grey.50",
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 40,
                            color: row.alarm
                              ? "#dc2626"
                              : row.hasData
                                ? row.accent
                                : "text.disabled",
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: row.alarm
                                ? "#fee2e2"
                                : row.hasData
                                  ? row.tint
                                  : "grey.100",
                              color: row.alarm
                                ? "#dc2626"
                                : row.hasData
                                  ? row.deep
                                  : "text.disabled",
                            }}
                          >
                            {row.icon}
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                              }}
                            >
                              <Typography
                                component="span"
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "0.9rem",
                                  color: row.alarm ? "#991b1b" : "text.primary",
                                }}
                              >
                                {row.label}
                              </Typography>
                              {row.alarm && (
                                <Box
                                  component="span"
                                  sx={{
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    letterSpacing: 0.5,
                                    textTransform: "uppercase",
                                    px: 0.625,
                                    py: 0.125,
                                    borderRadius: 999,
                                    bgcolor: "#dc2626",
                                    color: "#fff",
                                  }}
                                >
                                  Severe
                                </Box>
                              )}
                            </Box>
                          }
                          secondary={
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{
                                display: "block",
                                color: row.hasData
                                  ? "text.secondary"
                                  : "text.disabled",
                                fontStyle: row.hasData ? "normal" : "italic",
                                fontSize: "0.75rem",
                                mt: 0.25,
                              }}
                            >
                              {row.preview}
                            </Typography>
                          }
                        />
                        <ChevronRightIcon
                          fontSize="small"
                          sx={{ color: "text.disabled", ml: 1 }}
                        />
                      </ListItemButton>
                    </React.Fragment>
                  ))}
                </List>
              </SwipeableDrawer>
            );
          })()}

        {(() => {
          const panels = (
            <>
              {/* Allergies expanded panel */}
              <Collapse in={allergyOpen} unmountOnExit>
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderLeft: "4px solid",
                    borderColor:
                      allergyTone === "warning"
                        ? "warning.main"
                        : allergyTone === "error"
                          ? "error.main"
                          : "divider",
                    bgcolor:
                      allergyTone === "warning"
                        ? "warning.50"
                        : allergyTone === "error"
                          ? "error.50"
                          : "grey.50",
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                  }}
                >
                  {allergiesNone ? (
                    <CheckCircleIcon sx={{ color: "success.main", mt: 0.25 }} />
                  ) : (
                    <WarningAmberIcon
                      sx={{
                        color:
                          allergyTone === "error"
                            ? "error.main"
                            : allergyTone === "warning"
                              ? "warning.main"
                              : "text.secondary",
                        mt: 0.25,
                      }}
                    />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <PatientDetailListManager
                      spruceEntityId={spruceEntityId}
                      category="allergies"
                      label="Allergies"
                      placeholder="e.g. Penicillin — Severe (hives), or NKDA"
                      emptyText="No known allergies documented"
                    />
                  </Box>
                  <Tooltip title="Close">
                    <IconButton
                      size="small"
                      aria-label="Collapse allergies"
                      onClick={() => setExpanded(null)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Collapse>

              {renderTextPanel("primary_dx")}
              {renderTextPanel("current_meds")}

              {/* Encounters / visit notes expanded panel (read-only). Notes can
                be long, so the list scrolls inside a capped-height container. */}
              <Collapse in={expanded === "encounters"} unmountOnExit>
                <Box
                  sx={{
                    mt: 1,
                    p: 1.5,
                    borderLeft: "4px solid",
                    borderColor: "#0284c7",
                    bgcolor: "#f0f9ff",
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      maxHeight: "60vh",
                      overflowY: "auto",
                      pr: 0.5,
                    }}
                  >
                    <PatientEncountersPanel
                      encounters={encounters}
                      loading={encountersLoading}
                    />
                  </Box>
                  <Tooltip title="Close">
                    <IconButton
                      size="small"
                      aria-label="Collapse encounters"
                      onClick={() => setExpanded(null)}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Collapse>

              {renderTextPanel("family_history")}

              {/* Vitals & Intake expanded panel */}
              <Collapse in={vitalsExpanded} unmountOnExit>
                <Box
                  sx={{
                    mt: 1,
                    p: { xs: 1.25, sm: 1.5 },
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    maxHeight: { xs: "60vh", sm: "50vh" },
                    overflowY: "auto",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1.5,
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <MonitorHeartIcon color="primary" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Vitals & Intake
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: { xs: "100%", sm: "auto" },
                        justifyContent: { xs: "space-between", sm: "flex-end" },
                      }}
                    >
                      <ToggleButtonGroup
                        size="small"
                        value={unitSystem}
                        exclusive
                        onChange={(_, val) => {
                          if (val) setUnitSystem(val);
                        }}
                        aria-label="Unit system"
                      >
                        <ToggleButton
                          value="metric"
                          sx={{ px: 1.5, py: 0.25, fontSize: "0.7rem" }}
                        >
                          kg / cm / °C
                        </ToggleButton>
                        <ToggleButton
                          value="imperial"
                          sx={{ px: 1.5, py: 0.25, fontSize: "0.7rem" }}
                        >
                          lb / in / °F
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <Button
                        size="small"
                        startIcon={<EditIcon fontSize="small" />}
                        onClick={openVitals}
                      >
                        Edit
                      </Button>
                      <Tooltip title="Close">
                        <IconButton
                          size="small"
                          aria-label="Collapse vitals"
                          onClick={() => setExpanded(null)}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "repeat(2, 1fr)",
                        sm: "repeat(3, 1fr)",
                        md: "repeat(5, 1fr)",
                      },
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    {[
                      {
                        label: "Height",
                        value: displayHeight,
                        cat: "vitals_height",
                      },
                      {
                        label: "Weight",
                        value: displayWeight,
                        cat: "vitals_weight",
                      },
                      { label: "BMI", value: vitalBmi, cat: "" },
                      {
                        label: "Blood Pressure",
                        value: vitalBp,
                        cat: "vitals_bp",
                      },
                      {
                        label: "Pulse",
                        value: vitalPulse ? `${vitalPulse} bpm` : "",
                        cat: "vitals_pulse",
                      },
                      {
                        label: "Temperature",
                        value: displayTemp,
                        cat: "vitals_temp",
                      },
                      {
                        label: "Resp. Rate",
                        value: vitalResp ? `${vitalResp} /min` : "",
                        cat: "vitals_resp",
                      },
                      {
                        label: "SpO₂",
                        value: vitalSpo2 ? `${vitalSpo2}%` : "",
                        cat: "vitals_spo2",
                      },
                      {
                        label: "Pain",
                        value: vitalPain ? `${vitalPain} / 10` : "",
                        cat: "vitals_pain",
                      },
                      {
                        label: "Smoking",
                        value: vitalSmoking,
                        cat: "vitals_smoking",
                      },
                    ].map((v) => {
                      const meta = v.cat ? vitalsMeta(v.cat) : null;
                      return (
                        <Box
                          key={v.label}
                          sx={{
                            p: 1.5,
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            bgcolor: "grey.50",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            minHeight: 78,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                textTransform: "uppercase",
                                letterSpacing: 0.5,
                                fontSize: "0.65rem",
                                display: "block",
                              }}
                            >
                              {v.label}
                            </Typography>
                            {v.cat &&
                              renderHistoryButton(v.cat, v.label, {
                                dense: true,
                              })}
                          </Box>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: v.value ? "text.primary" : "text.disabled",
                              fontStyle: v.value ? "normal" : "italic",
                              mt: 0.25,
                            }}
                          >
                            {v.value || "—"}
                          </Typography>
                          {v.value && meta && (
                            <Tooltip title={meta.absolute} placement="top">
                              <Box
                                component="span"
                                tabIndex={0}
                                aria-label={`Recorded ${meta.absolute}`}
                                sx={{
                                  color: meta.color,
                                  fontSize: "0.65rem",
                                  mt: 0.5,
                                  display: "block",
                                  cursor: "help",
                                  outline: "none",
                                  "&:focus-visible": {
                                    textDecoration: "underline",
                                  },
                                }}
                              >
                                {meta.rel}
                              </Box>
                            </Tooltip>
                          )}
                          {v.value && !meta && v.cat === "" && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.disabled",
                                fontSize: "0.65rem",
                                mt: 0.5,
                                fontStyle: "italic",
                              }}
                            >
                              auto-calculated
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                  {renderLastEditedCaption(latestVitalDetail)}
                </Box>
              </Collapse>
            </>
          );
          // Desktop pills use inline Collapse panels; mobile AND the desktop
          // chart surface use the section editor dialog below (fullScreen on
          // mobile, centered modal on desktop) so both platforms behave alike.
          if (!isMobile && !chartSurface) return panels;
          // Section editor — purpose-built (not the desktop Collapse
          // panels). Vitals is handled by its own dedicated dialog from the
          // chart sheet row, so `expanded` here is always a text section.
          const titleMap: Record<ExpandedKey, string> = {
            allergies: "Allergies",
            primary_dx: TEXT_SECTION_CONFIG.primary_dx.label,
            current_meds: TEXT_SECTION_CONFIG.current_meds.label,
            family_history: TEXT_SECTION_CONFIG.family_history.label,
            vitals: "Vitals & Intake",
            encounters: "Encounters",
          };
          const sectionAccent: Record<ExpandedKey, string> = {
            allergies: "#dc2626",
            primary_dx: "#4f46e5",
            current_meds: "#d97706",
            family_history: "#059669",
            vitals: "#0d9488",
            encounters: "#0284c7",
          };
          const placeholderMap: Record<string, string> = {
            allergies: "e.g. Penicillin (severe), shellfish, or NKDA",
            primary_dx: TEXT_SECTION_CONFIG.primary_dx.placeholder,
            current_meds: TEXT_SECTION_CONFIG.current_meds.placeholder,
            family_history: TEXT_SECTION_CONFIG.family_history.placeholder,
          };
          const sec = expanded;
          const isAllergiesSec = sec === "allergies";
          const isTextSec =
            sec === "primary_dx" ||
            sec === "current_meds" ||
            sec === "family_history";
          const draftValue = isAllergiesSec
            ? allergyDraft
            : isTextSec
              ? textDrafts[sec]
              : "";
          const setDraftValue = (v: string) => {
            if (isAllergiesSec) setAllergyDraft(v);
            else if (isTextSec)
              setTextDrafts((prev) => ({ ...prev, [sec]: v }));
          };
          const saving = isAllergiesSec
            ? savingAllergies
            : isTextSec && savingSection === sec;
          const onSave = () => {
            if (isAllergiesSec) saveAllergies();
            else if (isTextSec) saveTextSection(sec);
          };
          const detailKey = isAllergiesSec
            ? "allergies"
            : isTextSec
              ? TEXT_SECTION_CONFIG[sec].category
              : "";
          const lastDetail = detailKey ? latestDetails[detailKey] : undefined;
          const placeholder =
            sec && placeholderMap[sec] ? placeholderMap[sec] : "";
          // Primary DX, Current Meds and Allergies are structured lists (one
          // row per item) — render their shared managers here so mobile matches
          // desktop exactly, instead of the legacy single free-text editor.
          const managerSec =
            sec === "primary_dx" || sec === "current_meds" || sec === "allergies"
              ? sec
              : null;
          // Encounters is a read-only list (no editor / save bar).
          const isEncounters = sec === "encounters";
          return (
            <Dialog
              fullScreen={isMobile}
              maxWidth="sm"
              fullWidth={!isMobile}
              open={!!expanded}
              onClose={closeMobileEditor}
              aria-labelledby="chart-section-editor-title"
              PaperProps={{ sx: { bgcolor: "background.default" } }}
              sx={drawerZIndex !== undefined ? { zIndex: drawerZIndex + 1 } : undefined}
            >
              {/* Top app bar */}
              <Box
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  bgcolor: "background.paper",
                  borderBottom: 1,
                  borderColor: "divider",
                  px: 1,
                  pt: "calc(env(safe-area-inset-top) + 4px)",
                  pb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  boxShadow: "0 1px 0 rgba(15, 23, 42, 0.04)",
                }}
              >
                <IconButton
                  edge="start"
                  aria-label="Back to chart"
                  onClick={closeMobileEditor}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: 22,
                      borderRadius: 2,
                      bgcolor: sec ? sectionAccent[sec] : "transparent",
                    }}
                  />
                  <Typography
                    id="chart-section-editor-title"
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      flex: 1,
                      minWidth: 0,
                      fontSize: "1.05rem",
                    }}
                    noWrap
                  >
                    {sec ? titleMap[sec] : ""}
                  </Typography>
                </Box>
                {sec &&
                  isTextSec &&
                  renderHistoryButton(
                    TEXT_SECTION_CONFIG[sec].category,
                    titleMap[sec],
                  )}
                {isAllergiesSec &&
                  renderHistoryButton("allergies", "Allergies")}
              </Box>
              {/* Scrollable body */}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  p: 2,
                  overflowY: "auto",
                }}
              >
                {isEncounters ? (
                  <PatientEncountersPanel
                    encounters={encounters}
                    loading={encountersLoading}
                  />
                ) : managerSec === "primary_dx" ? (
                  <PatientDiagnosisManager spruceEntityId={spruceEntityId} />
                ) : managerSec === "current_meds" ? (
                  <PatientDetailListManager
                    spruceEntityId={spruceEntityId}
                    category="current_medications"
                    label="Current Meds"
                    placeholder={placeholder}
                    emptyText="No current medications recorded"
                    secondaryLabel="Directions"
                  />
                ) : managerSec === "allergies" ? (
                  <PatientDetailListManager
                    spruceEntityId={spruceEntityId}
                    category="allergies"
                    label="Allergies"
                    placeholder={placeholder}
                    emptyText="No known allergies documented"
                  />
                ) : (
                  <>
                    <TextField
                      value={draftValue}
                      onChange={(e) => setDraftValue(e.target.value)}
                      multiline
                      minRows={10}
                      fullWidth
                      placeholder={placeholder}
                      autoFocus
                      disabled={saving}
                      inputProps={{
                        // 16px input prevents iOS Safari from auto-zooming on focus
                        style: { fontSize: "16px", lineHeight: 1.5 },
                        enterKeyHint: "enter",
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          alignItems: "flex-start",
                          bgcolor: "background.paper",
                          borderRadius: 2,
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "divider",
                        },
                        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "text.disabled",
                          },
                      }}
                    />
                    <Box sx={{ px: 0.5 }}>
                      {renderLastEditedCaption(lastDetail)}
                    </Box>
                  </>
                )}
              </Box>
              {/* Sticky bottom action bar */}
              <Box
                sx={{
                  position: "sticky",
                  bottom: 0,
                  zIndex: 2,
                  bgcolor: "background.paper",
                  borderTop: 1,
                  borderColor: "divider",
                  px: 2,
                  pt: 1,
                  pb: "calc(env(safe-area-inset-bottom) + 12px)",
                  display: "flex",
                  gap: 1,
                  boxShadow: "0 -1px 0 rgba(15, 23, 42, 0.04)",
                }}
              >
                {managerSec || isEncounters ? (
                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    onClick={closeMobileEditor}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    Done
                  </Button>
                ) : (
                  <>
                    <Button
                      fullWidth
                      size="large"
                      onClick={closeMobileEditor}
                      disabled={saving}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      fullWidth
                      size="large"
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={onSave}
                      disabled={saving}
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      {saving ? "Saving…" : "Save"}
                    </Button>
                  </>
                )}
              </Box>
            </Dialog>
          );
        })()}

        {/* Full edit-history panel for a single chart field. Shows every
          patient_details row for the category in reverse-chronological order
          with timestamp, author, saved value, and a simple add/remove diff
          against the previous revision. */}
        <Dialog
          open={!!historyCategory}
          onClose={closeHistory}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
          aria-labelledby="chart-history-title"
          sx={drawerZIndex !== undefined ? { zIndex: drawerZIndex + 1 } : undefined}
        >
          <DialogTitle
            id="chart-history-title"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                minWidth: 0,
              }}
            >
              <HistoryIcon fontSize="small" sx={{ color: "text.secondary" }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, lineHeight: 1.2 }}
                  noWrap
                >
                  {historyTitle} — Edit History
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {historyEntries.length}{" "}
                  {historyEntries.length === 1 ? "revision" : "revisions"},
                  newest first
                </Typography>
              </Box>
            </Box>
            <IconButton
              size="small"
              aria-label="Close history"
              onClick={closeHistory}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            {historyEntries.length === 0 ? (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No edits recorded for this field yet.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                {historyEntries.map((entry, idx) => {
                  const prev = historyEntries[idx + 1];
                  const iso = entry.createdAt || entry.created_at || "";
                  const when = iso ? new Date(iso) : null;
                  const meta = formatVitalAge(iso);
                  const isLatest = idx === 0;
                  return (
                    <Box
                      key={`${iso}-${idx}`}
                      sx={{
                        p: 2,
                        borderBottom: idx === historyEntries.length - 1 ? 0 : 1,
                        borderColor: "divider",
                        bgcolor: isLatest ? "primary.50" : "background.paper",
                        borderLeft: isLatest
                          ? "4px solid"
                          : "4px solid transparent",
                        borderLeftColor: isLatest
                          ? "primary.main"
                          : "transparent",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        {isLatest && (
                          <Typography
                            variant="caption"
                            sx={{
                              px: 0.75,
                              py: 0.1,
                              borderRadius: 1,
                              bgcolor: "primary.main",
                              color: "primary.contrastText",
                              fontWeight: 600,
                              letterSpacing: 0.3,
                              fontSize: "0.65rem",
                              textTransform: "uppercase",
                            }}
                          >
                            Current
                          </Typography>
                        )}
                        <Tooltip title={when ? dateFmtFull.format(when) : ""}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, cursor: "help" }}
                          >
                            {when ? dateFmtFull.format(when) : "Unknown time"}
                            {meta ? ` · ${meta.rel}` : ""}
                          </Typography>
                        </Tooltip>
                      </Box>
                      {renderHistoryValue(
                        entry,
                        prev,
                        entry.id || `${iso}-${idx}`,
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeHistory}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={vitalsOpen}
          onClose={() => setVitalsOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          sx={drawerZIndex !== undefined ? { zIndex: drawerZIndex + 1 } : undefined}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Edit Vitals & Intake</span>
            <ToggleButtonGroup
              size="small"
              value={unitSystem}
              exclusive
              onChange={(_, val) => {
                if (val) setUnitSystem(val);
              }}
              aria-label="Unit system"
            >
              <ToggleButton
                value="metric"
                sx={{ px: 1.5, py: 0.25, fontSize: "0.7rem" }}
              >
                Metric
              </ToggleButton>
              <ToggleButton
                value="imperial"
                sx={{ px: 1.5, py: 0.25, fontSize: "0.7rem" }}
              >
                US
              </ToggleButton>
            </ToggleButtonGroup>
          </DialogTitle>
          <DialogContent
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "1fr 1fr 1fr",
              },
              gap: { xs: 1.5, sm: 2.5 },
              pt: { xs: "12px !important", sm: "20px !important" },
              overflowY: "auto",
            }}
          >
            <TextField
              label={unitSystem === "metric" ? "Height (cm)" : "Height (in)"}
              type="number"
              value={vitalsDraft.height}
              onChange={(e) => updateDraft("height", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={unitSystem === "metric" ? "Weight (kg)" : "Weight (lb)"}
              type="number"
              value={vitalsDraft.weight}
              onChange={(e) => updateDraft("weight", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Blood Pressure"
              placeholder="e.g. 120/80"
              value={vitalsDraft.bp}
              onChange={(e) => updateDraft("bp", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Pulse (bpm)"
              type="number"
              value={vitalsDraft.pulse}
              onChange={(e) => updateDraft("pulse", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label={
                unitSystem === "metric"
                  ? "Temperature (°C)"
                  : "Temperature (°F)"
              }
              type="number"
              value={vitalsDraft.temp}
              onChange={(e) => updateDraft("temp", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Respiratory Rate (/min)"
              type="number"
              value={vitalsDraft.resp}
              onChange={(e) => updateDraft("resp", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="SpO₂ (%)"
              type="number"
              inputProps={{ min: 0, max: 100 }}
              value={vitalsDraft.spo2}
              onChange={(e) => updateDraft("spo2", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Pain Scale (0–10)"
              type="number"
              inputProps={{ min: 0, max: 10 }}
              value={vitalsDraft.pain}
              onChange={(e) => updateDraft("pain", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              label="Smoking Status"
              value={vitalsDraft.smoking}
              onChange={(e) => updateDraft("smoking", e.target.value)}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">
                <em>Not recorded</em>
              </MenuItem>
              <MenuItem value="Never">Never</MenuItem>
              <MenuItem value="Former">Former</MenuItem>
              <MenuItem value="Current">Current</MenuItem>
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVitalsOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={saveVitals}
              disabled={savingVitals}
            >
              {savingVitals ? "Saving..." : "Save Vitals"}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={!!errorMsg}
          autoHideDuration={5000}
          onClose={() => setErrorMsg(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity="error"
            variant="filled"
            onClose={() => setErrorMsg(null)}
            sx={{ width: "100%" }}
          >
            {errorMsg}
          </Alert>
        </Snackbar>
      </Paper>
    );
  },
);

PatientClinicalStrip.displayName = "PatientClinicalStrip";

export default PatientClinicalStrip;
